import { useIsSplitUnavailable, useSplitEnabled } from '@buffer-mono/features'

/**
 * Resolves the `deprecate-account-service` treatment for the channel-connect
 * flow, which picks the OAuth redirect URI.
 *
 * `useSplitEnabled` reports `isReady: true` with the default treatment while
 * the Split client is still `null`, which suits callers that render
 * immediately but would lock connect flows into the legacy account.buffer.com
 * URI. This hook waits for the SDK to settle instead: `FeaturesWrapper`
 * publishes the client on SDK_READY or on SDK_READY_TIMED_OUT (bounded by the
 * SDK's `startup.readyTimeout`), so a missing client only means "not settled
 * yet". A timed-out SDK settles on "off"; if it recovers and emits SDK_READY
 * later, the real treatment applies from then on. When no client will ever
 * exist (the org query settled without an organization), it settles on "off".
 *
 * The flag targets on the `service` attribute, so the localStorage treatment
 * cache (keyed by flag name only) is skipped: a value cached for another
 * service must not decide this one.
 *
 * @see CORE-6102, CORE-7824
 */
export function useDeprecateAccountServiceTreatment(service: string): {
  /** The treatment is settled and authorization info can be requested. */
  isResolved: boolean
  /** Use the callbacks service redirect URI instead of account.buffer.com. */
  useCallbacksService: boolean
} {
  const { isEnabled, isReady, isTimedout, hasClient } = useSplitEnabled(
    'deprecate-account-service',
    { attributes: { service }, skipCache: true },
  )

  const isSplitUnavailable = useIsSplitUnavailable()

  // With a client, `isReady` is true once the SDK is ready or has timed out.
  const isResolved = (hasClient && isReady) || isSplitUnavailable

  return {
    isResolved,
    useCallbacksService: isResolved && !isTimedout && isEnabled,
  }
}
